import React, { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { i18n } from '../../translate/i18n';
import * as actions from "../../store/actions";
import { sortBy } from 'lodash';

import {
  Container, Typography, Divider, AppBar, Toolbar, Box
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { CheckCircleRounded, CancelRounded, ArrowBack } from '@mui/icons-material';

import geequipLOGO from '../../assets/Icone-png-16.png'

import Spinner from '../../components/Spinner/index';

import { renderStatusIcon, renderStatusReason, renderStatusColor } from '../../utils/statusManipulation'
import DataLabel from './components/DataLabel'
import LanguageSelection from '../Login/components/LanguageSelection';
import FileList from './components/FileList';

const useStyles = makeStyles(theme => ({
  appBar: {
    backgroundColor: 'green',
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between'
  },
}));

const FormEquipmentsPublic = (props) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const equipId = props.match.params.id;

  const fetchEquipment = useCallback(
    () => dispatch(actions.fetchEquipmentsPublicByIdWithServicesReplicate(equipId))
  );

  // get from redux
  const equipment = useSelector(state => state.equipmentsPublic.equipment);
  const fetchError = useSelector(state => state.equipmentsPublic.error);
  const loadingEquipments = useSelector(state => state.equipmentsPublic.loading);

  // check if is newEntrance or editing
  useEffect(() => {
    if (equipId) {
      fetchEquipment();
    }
  }, [equipId])

  useEffect(() => {
    if (fetchError) {
      localStorage.setItem('redirectTo', `/form-equip-public/${equipId}`);
      props.history.push('/login')
    }
  }, [fetchError])

  const handleEquipmentServices = (serv) => {
    const service = equipment.equipmentServicesReplicate && equipment.equipmentServicesReplicate.find(s => s.equipmentServiceId === serv._id)
    const label = handleEquipmentServicesLabel(serv);

    return (
      <>
        <div key={serv._id} style={{ display: 'flex', flexDirection: 'column', padding: '0.25rem 0' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div>{`${serv.serviceType.name}  ${serv.frequency.name}`}</div>
            {serv.mandatory &&
              <div style={{ marginLeft: '1rem', backgroundColor: '#808080', fontSize: '0.7rem', padding: '0.25rem', border: '5px', borderRadius: '5px', color: '#fff' }}>
                {i18n.t('divFromEquipService.mandatory')}
              </div>}
          </div>
          <div style={styles.subtitle}>
            {label}
          </div>
        </div>
        {(service && service.status) ?
          <CheckCircleRounded style={{ color: '#6bbd6b' }} />
          : <CancelRounded style={{ color: "#e57878" }} />}
      </>
    )
  }

  const handleEquipmentServicesLabel = (serv) => {
    const service = equipment.equipmentServicesReplicate && equipment.equipmentServicesReplicate.find(s => s.equipmentServiceId === serv._id)
    let label = '';

    const status = (service && service.situation) ? service.situation.status : '';

    if (!service) {
      label = i18n.t('divFromEquipService.noServices');
    } else if (status === 'onDemand') {
      label = i18n.t('divFromEquipService.onDemand');
    } else if (status === 'noServices') {
      label = i18n.t('divFromEquipService.noServices');
    } else if (status === 'lastDay') {
      label = i18n.t('divFromEquipService.lastDay');
    } else if (status === 'remaining') {
      const translateFunction = i18n.t('divFromEquipService.remaining');
      label = translateFunction(new Date(service.expirationDate).toLocaleDateString('PT-br'), service.situation.days);
    } else if (status === 'inDelay') {
      const translateFunction = i18n.t('divFromEquipService.inDelay');
      label = translateFunction(new Date(service.expirationDate).toLocaleDateString('PT-br'), service.situation.days);
    }

    return label;
  }

  const handleEquipmentServicesReason = (serv) => {
    const service = equipment.equipmentServicesReplicate && equipment.equipmentServicesReplicate.find(s => s.equipmentServiceId === serv._id)
    let note = '';
    let noteFunction = () => '';

    if (!service) {
      note = i18n.t('typographyformEquipServ.notDone');
    }

    if (service && !service.status && service.note && service.note.hasActionsOpenedOrOverdue > 0) {
      noteFunction = i18n.t('typographyformEquipServ.hasActionsOpenedOrOverdue');
      note = noteFunction(service.note.hasActionsOpenedOrOverdue);
    }

    if (service && !service.status && service.note && service.note.inDelay > 0) {
      noteFunction = i18n.t('typographyformEquipServ.inDelay');
      note = noteFunction(service.note.inDelay);
    }

    return note;
  }

  const goToHome = () => {
    props.history.push('/');
  }

  return (
    <>
      <AppBar className={classes.appBar}>
        <Toolbar className={classes.toolbar}>
          <div style={styles.logoContainer}>
            <img src={geequipLOGO} style={styles.logo} />
            <div style={styles.titleContainer}>
              <Typography style={{ fontWeight: 'bold', fontSize: 22 }} className={classes.title} noWrap>ge</Typography>
              <Typography style={{ fontSize: 22 }}>equip</Typography>
            </div>
          </div>
          <LanguageSelection />
        </Toolbar>
      </AppBar>

      {loadingEquipments && <Spinner overlay />}

      <Container maxWidth="sm" style={styles.container}>
        <div style={{ display: 'flex', alignItems: 'center' }} onClick={goToHome}>
          <ArrowBack />
        </div>

        <div style={styles.photoContainer}>
          {(!equipment.photo || loadingEquipments) ?
            <div style={styles.avatar}>{i18n.t('avatarFormEquip.photo')}</div>
            : <img style={styles.photo} src={loadingEquipments ? '' : equipment.photo?.url} />}
        </div>

        <div style={styles.equipModelContainer}>
          <div style={styles.equipModelContent}>
            <div style={styles.equipName}>{equipment.equipmentModel?.name}</div>
            <div style={styles.equipStatus}>{renderStatusIcon(equipment)}</div>
          </div>
          <div style={{ color: renderStatusColor(equipment), fontWeight: 'bold', textAlign: 'center' }}>{renderStatusReason(equipment)}</div>
        </div>

        <div style={styles.formContent}>
          <Divider />
          {equipment?.equipmentModel && sortBy(equipment?.equipmentModel?.equipmentServices, ['serviceType.name', 'frequency.name']).map((serv, ind) => {
            return (
              <div key={serv._id}>
                <div style={styles.listItem}>
                  {handleEquipmentServices(serv)}
                </div>
                {handleEquipmentServicesReason(serv) !== '' && handleEquipmentServicesLabel(serv) !== handleEquipmentServicesReason(serv) &&
                  <div style={styles.listItemReason}>
                    {handleEquipmentServicesReason(serv)}
                  </div>}
                <Divider />
              </div>
            )
          })}
        </div>

        <div style={styles.equipInfoContainer}>
          <div style={styles.equipInfoTitle}>{i18n.t('autocompleteFormEquip.equipmentAttachments')}</div>
          {!equipment?.attachments || equipment?.attachments.length === 0 ?
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 2 }}>
              {i18n.t('autocompleteFormEquip.noAttachments')}
            </Box> :
            <Box>
              {equipment?.attachments.map((attachment) => (
                <FileList attachment={attachment} key={attachment._id} />
              ))}
            </Box>
          }
        </div>

        <div style={styles.equipInfoContainer}>
          <div style={styles.equipInfoTitle}>{i18n.t('autocompleteFormEquip.equipmentModelAttachments')}</div>
          {!equipment?.equipmentModel?.attachments || equipment?.equipmentModel?.attachments.length === 0 ?
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 2 }}>
              {i18n.t('autocompleteFormEquip.noAttachments')}
            </Box> :
            <Box>
              {equipment?.equipmentModel?.attachments.map((attachment) => (
                <FileList attachment={attachment} key={attachment._id} />
              ))}
            </Box>
          }
        </div>

        <div style={styles.equipInfoContainer}>
          <div style={styles.equipInfoTitle}>{i18n.t('autocompleteFormEquip.equipInfo')}</div>
          <DataLabel title={i18n.t('autocompleteFormEquip.local')} value={equipment.place?.path} loading={loadingEquipments} />
          <DataLabel title={i18n.t('textFieldFormEquip.serialNumber')} value={equipment?.serialNumber} loading={loadingEquipments} />
          <DataLabel title={i18n.t('textFieldFormEquip.name')} value={equipment?.description} loading={loadingEquipments} />
          <DataLabel title={i18n.t('autocompleteFormEquip.area')} value={equipment.area?.name} loading={loadingEquipments} />
          <DataLabel title={i18n.t('autocompleteFormEquip.company')} value={equipment.company?.name} loading={loadingEquipments} />
          <DataLabel title={i18n.t('textFieldFormEquip.brand')} value={equipment?.brand} loading={loadingEquipments} />
          <DataLabel title={i18n.t('textFieldFormEquip.reference')} value={equipment?.reference} loading={loadingEquipments} />
        </div>

      </Container>
    </>
  )
}

export default FormEquipmentsPublic;

const styles = {
  container: {
    marginTop: 80,
  },
  logoContainer: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer'
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  logo: {
    width: 36,
    marginRight: 10,
  },
  avatar: {
    display: 'flex',
    width: 195,
    height: 195,
    margin: '1.25rem 0',
    border: '1px solid grey',
    borderRadius: '.5rem',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'grey',
    fontSize: '1rem',
    backgroundColor: '#eee'
  },
  photo: {
    maxWidth: 195,
    maxHeight: 195,
    margin: '1.25rem 0',
  },
  photoContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    flex: 1,
  },
  equipModelContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  equipModelContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '.25rem 0'
  },
  equipStatus: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  equipName: {
    fontSize: '1.25rem',
    marginRight: '.5rem',
  },
  formContent: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: '1rem',
  },
  listItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0.5rem 0.5rem'
  },
  listItemReason: {
    display: 'flex',
    alignItems: 'center',
    padding: '0 0 .75rem 0.5rem',
    fontSize: '.9rem',
  },
  equipInfoContainer: {
    margin: '1rem 0 2rem 0',
  },
  equipInfoTitle: {
    // textDecoration: 'underline',
    padding: '1rem',
    backgroundColor: '#eee',
    color: 'grey'
  },
  subtitle: {
    fontSize: '0.7rem',
    color: 'grey',
  },
}
